import { useCallback, useMemo, useRef, useEffect } from 'react';
import { useFormContext } from 'react-form';
import type { SelfSignUpCaptureLeadInput } from '~/libs/gql/types';
import { validEmail } from '~/utils/forms';
import { useCaptureSelfSignUpLeadMutation } from '~/libs/gql/mutations/self_sign_up/captureSelfSignUpLeadMutation.generated';
import { debounce } from 'lodash';
import type { SelfSignUpForm } from '../types';
import type { Steps } from '../helpers/validateFormFields';

export const useCaptureLead = (validationStep: Steps) => {
  const startSignupTimestamp = useMemo(() => new Date().toISOString(), []);

  const [captureSelfSignUpLead] = useCaptureSelfSignUpLeadMutation();

  const { values } = useFormContext<SelfSignUpForm>();

  const isValidAttributes = useCallback((input: SelfSignUpCaptureLeadInput['input']) => !!input.email && !validEmail(input.email), []);

  const handleCaptureLead = useCallback(() => {
    const input: SelfSignUpCaptureLeadInput['input'] = {
      city: values.address?.city || '',
      companyName: values.restaurantName || '',
      country: values.address?.country || '',
      email: values.email || '',
      firstName: values.firstName || '',
      lastName: values.lastName || '',
      phone: values.phone?.inputValue || '',
      postalCode: values.address?.postalCode || '',
      productPlan: values.productPlan || '',
      signupAbandonedAt: new Date().toISOString(),
      signupAbandonedStep: validationStep,
      signupStartedAt: startSignupTimestamp,
      state: values.address?.regionCode || '',
      street: values.address?.street || '',
    };

    if (!isValidAttributes(input)) return;

    void captureSelfSignUpLead({
      variables: {
        input: {
          input,
        },
      },
    });
  }, [isValidAttributes, values, validationStep, startSignupTimestamp, captureSelfSignUpLead]);

  const handleCaptureLeadRef = useRef(handleCaptureLead);
  handleCaptureLeadRef.current = handleCaptureLead;

  const debouncedCaptureLead = useMemo(
    () => debounce(() => handleCaptureLeadRef.current(), 500),
    [],
  );

  useEffect(() => () => debouncedCaptureLead.cancel(), [debouncedCaptureLead]);

  return debouncedCaptureLead;
};
